import { useContext, useEffect, useMemo, useRef, useState } from 'react';
import { NavLink, useLocation, useNavigate } from 'react-router-dom';
import ArtworkIcon from "src/assets/all-icons/artwork-icon.jsx";
import BoardIcon from "src/assets/all-icons/board-icon.jsx";
import CollectionIcon from "src/assets/all-icons/collection-icon.jsx";
import CommunityIcon from "src/assets/all-icons/community-icon.jsx";

import ArtistIcon from '@/assets/all-icons/artist-icon';
import ArtlistIcon from '@/assets/all-icons/artlist-icon';
import config from '@/config';
import ProfileDataContext from '@/context/ProfileDataContext';
import useQuery from '@/hooks/useQuery';
import useTranslations from '@/i18n/useTranslations';
import Text from '@/shared-components/text/components/Text';

import './styles/ProfileNavigation.css';

const BOARD_TAB_NAME = 'Board';

function hasContent(value) {
  if (!value) return false;

  if (typeof value === 'number') {
    return value > 0;
  }

  if (Array.isArray(value)) {
    return value.length > 0;
  }

  if (typeof value === 'object') {
    if ('count' in value) return Boolean(value.count);
    if ('total' in value) return Boolean(value.total);
    if ('length' in value && typeof value.length === 'number') {
      return value.length > 0;
    }

    return Object.keys(value).length > 0;
  }

  return Boolean(value);
}

function shouldShowBoardTab({ internal, hasPosts, profileHasSuartePlus, userType }) {
  const normalizedType = (userType || "").toLowerCase();
  if (normalizedType === "gallery") {
    return internal || hasPosts;
  }

  if (!profileHasSuartePlus) {
    return internal;
  }

  if (internal) {
    return true;
  }

  return hasPosts;
}

function ProfileNavigationTab({ to, tabName, tabIcon, tabCount, isFirst }) {
  const location = useLocation();
  const splittedUrl = location.pathname.split("/");
  const isUserPage = splittedUrl.length === 3 && splittedUrl[1] === "user";

  return (
    <>
      <button className={`profile-navigation__tab ${tabCount === 1 ? "profile-navigation__one-tab-link": ""}`}>
        <NavLink
          className={({ isActive }) =>
            isActive || (isFirst && isUserPage)
              ? "profile-navigation__tab-link active hover-icon-animation"
              : "profile-navigation__tab-link hover-icon-animation"
          }
          to={to}
          aria-label={tabName}
          preventScrollReset
          state={location.state}
          // removed replace so this is a PUSH, not a REPLACE
        >
          <div>
            {tabIcon}

            <Text className="profile-navigation__tab-name" paragraph extraSmall>
              {tabName}
            </Text>
          </div>
        </NavLink>
      </button>
    </>
  );
}

export function GalleryProfileNavigation({ userProfileInfo, internal, profileHasSuartePlus }) {
  const navigate = useNavigate();
  const t = useTranslations('common.profile.navigation');
  // const navigationContainerRef = useRef(null);
  const { artworks, posts, artlists, artists } = userProfileInfo.data;

  const hasArtworks = hasContent(artworks);
  const hasPosts = hasContent(posts);
  const hasArtlists = hasContent(artlists);
  const hasArtists = hasContent(artists);
  const boardTabVisible = shouldShowBoardTab({
    internal,
    hasPosts,
    profileHasSuartePlus,
    userType: "gallery",
  });

  const totalTabs = useMemo(
    () => [hasArtworks, hasArtists, hasArtlists, boardTabVisible].filter(Boolean).length,
    [hasArtworks, hasArtists, hasArtlists, boardTabVisible]
  );

  const firstAvailableTab = useMemo(() => {
    if (hasArtworks) return "artworks";
    if (hasArtists) return "artists";
    if (hasArtlists) return "artlists";
    if (boardTabVisible) return "board";
    return null;
  }, [hasArtlists, hasArtists, hasArtworks, boardTabVisible]);

  const location = useLocation();

  useEffect(() => {
    const currentURL = location.pathname;
    const splittedUrl = currentURL.split("/");
    const lastTag = splittedUrl.slice(-1)[0];
    const knownTabs = ["artworks", "board", "artlists", "artists", "drafts", "subscriptions"];
    const isKnownTab = knownTabs.includes(lastTag);

    if (isKnownTab) {
      return;
    }

    if (firstAvailableTab) {
      navigate(firstAvailableTab, { replace: true, state: location.state });
    }
  }, [firstAvailableTab, location.pathname, location.state, navigate]);

  return (
    <div className="profile-navigation__container">
      {(internal || hasArtworks) ? <ProfileNavigationTab
        to="artworks"
        tabName={t('tabs.artworks')}
        tabIcon={<ArtworkIcon className="profile-navigation__tab-icon artworks hover-icon-animation__icon" />}
        tabCount={totalTabs}
        isFirst={true}
      /> : ""}

      {(internal || hasArtists) ? <ProfileNavigationTab
        to="artists"
        tabName={t('tabs.artists')}
        tabIcon={<ArtistIcon className="profile-navigation__tab-icon artists hover-icon-animation__icon" />}
        tabCount={totalTabs}
      /> : ""}

      {(internal || hasArtlists) ? <ProfileNavigationTab
        to="artlists"
        tabName={t('tabs.artlists')}
        tabIcon={<ArtlistIcon className="profile-navigation__tab-icon artlists hover-icon-animation__icon" />}
        tabCount={totalTabs}
      /> : ""}

      {boardTabVisible ? <ProfileNavigationTab
        to="board"
        tabName={BOARD_TAB_NAME}
        tabIcon={<BoardIcon className="profile-navigation__tab-icon board hover-icon-animation__icon" />}
        tabCount={totalTabs}
      /> : ""}
    </div>
  );

}

export function ArtistProfileNavigation({ userProfileInfo, internal, profileHasSuartePlus }) {
  const navigate = useNavigate();
  const navigationContainerRef = useRef(null);
  const t = useTranslations('common.profile.navigation');

  const { artworks, collections, thoughts, ratings, artlists, posts }= userProfileInfo.data;

  const hasArtworks = hasContent(artworks);
  const hasCollections = hasContent(collections);
  const hasThoughts = hasContent(thoughts);
  const hasRatings = hasContent(ratings);
  const hasCommunity = hasThoughts || hasRatings;
  const hasArtlists = hasContent(artlists);
  const hasPosts = hasContent(posts);
  const boardTabVisible = shouldShowBoardTab({
    internal,
    hasPosts,
    profileHasSuartePlus,
    userType: "artist",
  });

  const tempTabCount = useMemo(
    () => [hasArtworks, hasCollections, hasCommunity, hasArtlists, boardTabVisible].filter(Boolean).length,
    [hasArtworks, hasCollections, hasCommunity, hasArtlists, boardTabVisible]
  );

  const firstAvailableTab = useMemo(() => {
    if (hasArtworks) return "artworks";
    if (hasCollections) return "collection";
    if (hasCommunity) return "community";
    if (hasArtlists) return "artlists";
    if (boardTabVisible) return "board";
    return null;
  }, [hasArtlists, hasArtworks, hasCollections, hasCommunity, boardTabVisible]);

  const location = useLocation();
  const [firstPage, setFirstPage] = useState("");

  useEffect(() => {
    const nextFirstPage = firstAvailableTab || "";
    if (nextFirstPage !== firstPage) {
      setFirstPage(nextFirstPage);
    }
  }, [firstAvailableTab, firstPage]);

  useEffect(() => {
    const currentURL = location.pathname;
    const splittedUrl = currentURL.split("/");
    const lastTag = splittedUrl.slice(-1)[0];
    const knownTabs = ["artworks", "collection", "community", "artlists", "board", "subscriptions"];
    const isKnownTab = knownTabs.includes(lastTag);

    if (isKnownTab) {
      return;
    }
    
    if (firstAvailableTab) {
      navigate(firstAvailableTab, { replace: true, state: location.state });
    }
  }, [
    firstAvailableTab,
    location.pathname,
    location.state,
    navigate,
  ]);

  return (
    <div className="profile-navigation__container"  ref={navigationContainerRef}>
      {(internal || hasArtworks) ? <ProfileNavigationTab
        to="artworks"
        tabName={t('tabs.artworks')}
        tabIcon={<ArtworkIcon className="profile-navigation__tab-icon artworks hover-icon-animation__icon" />}
        tabCount={tempTabCount}
        isFirst={"artworks" === firstPage}
      /> : ""}

      {(internal || hasCollections) ? <ProfileNavigationTab
        to="collection"
        tabName={t('tabs.collection')}
        tabIcon={<CollectionIcon className="profile-navigation__tab-icon collection hover-icon-animation__icon" />}
        tabCount={tempTabCount}
        isFirst={"collection" === firstPage}
      /> : ""}

      {(internal || hasCommunity) ? <ProfileNavigationTab
        to="community"
        tabName={t('tabs.community')}
        tabIcon={<CommunityIcon className="profile-navigation__tab-icon community hover-icon-animation__icon" />}
        tabCount={tempTabCount}
        isFirst={"community" === firstPage}
      /> : ""}

      {(internal || hasArtlists) ? <ProfileNavigationTab
        to="artlists"
        tabName={t('tabs.artlists')}
        tabIcon={<ArtlistIcon className="profile-navigation__tab-icon artlists hover-icon-animation__icon" />}
        tabCount={tempTabCount}
        isFirst={"artlists" === firstPage}
      /> : ""}
      
      {/* === NEW: Board tab for artist === */}
      {boardTabVisible ? <ProfileNavigationTab
        to="board"
        tabName={BOARD_TAB_NAME}
        tabIcon={<BoardIcon className="profile-navigation__tab-icon board hover-icon-animation__icon" />}
        tabCount={tempTabCount}
        isFirst={"board" === firstPage}
      /> : ""}
    </div>
  );
}

export function CollectorProfileNavigation({ userProfileInfo, internal, profileHasSuartePlus }) {
  const navigate = useNavigate();
  const navigationContainerRef = useRef(null);
  const t = useTranslations('common.profile.navigation');

  const { collections, ratings, artlists, thoughts, posts } = userProfileInfo.data;

  const hasCollections = hasContent(collections);
  const hasRatings = hasContent(ratings);
  const hasThoughts = hasContent(thoughts);
  const hasCommunity = hasRatings || hasThoughts;
  const hasArtlists = hasContent(artlists);
  const hasPosts = hasContent(posts);
  const boardTabVisible = shouldShowBoardTab({
    internal,
    hasPosts,
    profileHasSuartePlus,
    userType: "collector",
  });

  const tempTabCount = useMemo(
    () => [hasCollections, hasCommunity, hasArtlists, boardTabVisible].filter(Boolean).length,
    [hasCollections, hasCommunity, hasArtlists, boardTabVisible]
  );

  const firstAvailableTab = useMemo(() => {
    if (hasCollections) return "collection";
    if (hasCommunity) return "community";
    if (hasArtlists) return "artlists";
    if (boardTabVisible) return "board";
    return null;
  }, [hasArtlists, hasCollections, hasCommunity, boardTabVisible]);

  const location = useLocation();
  const [firstPage, setFirstPage] = useState("");

  useEffect(() => {
    const nextFirstPage = firstAvailableTab || "";
    if (nextFirstPage !== firstPage) {
      setFirstPage(nextFirstPage);
    }
  }, [firstAvailableTab, firstPage]);

  useEffect(() => {
    const currentURL = location.pathname;
    const splittedUrl = currentURL.split("/");
    const lastTag = splittedUrl.slice(-1)[0];
    const knownTabs = ["collection", "community", "artlists", "board", "subscriptions"];
    const isKnownTab = knownTabs.includes(lastTag);

    if (isKnownTab) {
      return;
    }

    if (firstAvailableTab) {
      navigate(firstAvailableTab, { replace: true, state: location.state });
    }
  }, [
    firstAvailableTab,
    location.pathname,
    location.state,
    navigate
  ]);

  return (
    <div className="profile-navigation__container"  ref={navigationContainerRef}>
      {(internal || hasCollections) ? <ProfileNavigationTab
        to="collection"
        tabName={t('tabs.collection')}
        tabIcon={<CollectionIcon className="profile-navigation__tab-icon collection hover-icon-animation__icon" />}
        tabCount={tempTabCount}
        isFirst={"collection" === firstPage}
      /> : ""}

      {(internal || hasCommunity) ? <ProfileNavigationTab
        to="community"
        tabName={t('tabs.community')}
        tabIcon={<CommunityIcon className="profile-navigation__tab-icon community hover-icon-animation__icon" />}
        tabCount={tempTabCount}
        isFirst={"community" === firstPage}
      /> : ""}

      {(internal || hasArtlists) ? <ProfileNavigationTab
        to="artlists"
        tabName={t('tabs.artlists')}
        tabIcon={<ArtlistIcon className="profile-navigation__tab-icon artlists hover-icon-animation__icon" />}
        tabCount={tempTabCount}
        isFirst={"artlists" === firstPage}
      /> : ""}

      {boardTabVisible ? <ProfileNavigationTab
        to="board"
        tabName={BOARD_TAB_NAME}
        tabIcon={<BoardIcon className="profile-navigation__tab-icon board hover-icon-animation__icon" />}
        tabCount={tempTabCount}
        isFirst={"board" === firstPage}
      /> : ""}
    </div>
  );
}

export default function ProfileNavigation({ fetchData }) {
  const { profileData, internal } = useContext(ProfileDataContext);
  const fetchUrl = `${config.apis.api.url}/user/userProfile/${profileData._id}`;
  const query = { user_type: fetchData.user_type };
  const { queryData: userProfileInfo } = useQuery(`${profileData._id}_userProfileInfo`, fetchUrl, query, {
    expiresIn: ["10", "minutes"]
  });
  const profileHasSuartePlus = Boolean(profileData?.suartePlus?.isActive);

  switch(fetchData.user_type) {
  case "gallery": 
    return (
      <>
        {
          userProfileInfo && (
            <>
              <GalleryProfileNavigation userProfileInfo={userProfileInfo.data} internal={internal} profileHasSuartePlus={profileHasSuartePlus} />
            </>
          )
        }
      </>
    );
  case "artist":
    return (
      <>
        {userProfileInfo && (
          <>
            <ArtistProfileNavigation userProfileInfo={userProfileInfo.data} internal={internal} profileHasSuartePlus={profileHasSuartePlus} />
          </>
        )
        }
      </>
    );
  case "collector": 
    return (
      <>
        {
          userProfileInfo && (
            <>
              <CollectorProfileNavigation userProfileInfo={userProfileInfo.data} internal={internal} profileHasSuartePlus={profileHasSuartePlus} />
            </>
          )
        }
      </>
    );
  default:
    return (<></>);
  }
}
